import React, { createContext, useContext, useState, useEffect, FC, ReactNode, startTransition } from 'react';
import axios from 'axios';
import { API_ENDPOINT } from '../apiEndpoints';

interface User {
  id: number;
  email: string;
  role: string;
  username?: string;
  artistname?: string;
  supportername?: string;
  fileurl?: string;
  profile_photo_url?: string;
  bio?: string;
  current_account_type: 'artist' | 'supporter' | 'user';
  current_artist_id?: number | null;
  last_selected_artist_id?: number | null;
  associated_artist_ids?: number[];
  location?: string;
  date_of_birth?: string;
  dateOfBirth?: string;
  first_name?: string;
  firstName?: string;
  last_name?: string;
  lastName?: string;
  website?: string;
  isArtist?: boolean;
  isartist?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; user: User }>;
  loginWithGoogle: (
    idToken: string,
    options?: {
      role?: 'artist' | 'supporter';
      promoCode?: string;
      artistname?: string;
      username?: string;
      first?: string;
      last?: string;
      location?: string;
      dob?: string;
    }
  ) => Promise<{ success: boolean; user: User }>;
  register: (email: string, password: string, role: string) => Promise<{ success: boolean; user: User }>;
  loginWithToken: (token: string) => Promise<{ success: boolean; user: User }>;
  logout: () => void;
  updateUser: (user: User) => void;
  switchAccountType: (accountType: string, artistId?: number | null) => Promise<{ success: boolean }>;
  registerLogoutCallback: (callback: () => void) => () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

type LogoutCallback = () => void;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const shouldForceLogin = (): boolean => {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('forceLogin') === '1';
};

// Paths that must never trigger a refresh-and-retry (a 401 from these is a
// real auth failure, not an expired access token to silently recover from).
const REFRESH_EXEMPT_PATH_FRAGMENTS = ['/auth/login', '/auth/register', '/auth/google', '/auth/refresh'];

// Module-level (not component state) so every axios caller across the app
// shares one in-flight refresh instead of firing a parallel /auth/refresh
// call per simultaneous 401.
let refreshPromise: Promise<string> | null = null;

async function performTokenRefresh(): Promise<string> {
  const storedRefreshToken = localStorage.getItem('refreshToken');
  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }
  const response = await axios.post(`${API_ENDPOINT}/auth/refresh`, { refreshToken: storedRefreshToken });
  const payload = (response?.data?.data) ?? response?.data;
  const newToken: string | undefined = payload?.token;
  const newRefreshToken: string | undefined = payload?.refreshToken;
  if (!newToken || !newRefreshToken) {
    throw new Error('Refresh did not return a new token pair');
  }
  localStorage.setItem('token', newToken);
  localStorage.setItem('refreshToken', newRefreshToken);
  axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  return newToken;
}

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(() => {
    if (shouldForceLogin()) return null;
    return localStorage.getItem('token');
  });
  const [onLogoutCallbacks, setOnLogoutCallbacks] = useState<LogoutCallback[]>([]);

  const getRoleFromToken = (jwtToken: string | null): string | null => {
    if (!jwtToken) return null;
    try {
      const parts = jwtToken.split('.');
      if (parts.length < 2) return null;
      const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      const json = decodeURIComponent(
        atob(base64)
          .split('')
          .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      const payload = JSON.parse(json);
      return payload?.role ?? null;
    } catch {
      return null;
    }
  };

  // Set up axios interceptor for authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common['Authorization'];
    }
  }, [token]);

  // Transparently refreshes an expired access token and retries the failed
  // request on any 401 (see middleware/auth.js — 401 is reserved for
  // "not authenticated", never for permission-denied, so this never masks a
  // real 403). This is what lets a stale 24h access token recover silently
  // instead of forcing the user back to the login screen on every app launch.
  useEffect(() => {
    const interceptorId = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const status = axios.isAxiosError(error) ? error.response?.status : null;
        const originalRequest = error.config as (typeof error.config & { _retry?: boolean }) | undefined;
        const url: string = originalRequest?.url || '';
        const isExempt = REFRESH_EXEMPT_PATH_FRAGMENTS.some((p) => url.includes(p));

        if (status !== 401 || isExempt || !originalRequest || originalRequest._retry || !localStorage.getItem('refreshToken')) {
          return Promise.reject(error);
        }

        originalRequest._retry = true;
        try {
          if (!refreshPromise) {
            refreshPromise = performTokenRefresh().finally(() => { refreshPromise = null; });
          }
          const newToken = await refreshPromise;
          setToken(newToken);
          originalRequest.headers = { ...originalRequest.headers, Authorization: `Bearer ${newToken}` };
          return axios(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          setToken(null);
          setUser(null);
          return Promise.reject(error);
        }
      }
    );
    return () => { axios.interceptors.response.eject(interceptorId); };
  }, []);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      if (shouldForceLogin()) {
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');
          localStorage.removeItem('betaAccess');
          localStorage.removeItem('betaAccessTimestamp');
          sessionStorage.clear();
        } catch (error) {
          console.error('Failed to clear stored auth state during forceLogin:', error);
        }
        setUser(null);
        setToken(null);
        setLoading(false);
        return;
      }

      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken) {
        try {
          // Verify token with backend (pass token explicitly to avoid effect timing issues)
          const response = await axios.get(`${API_ENDPOINT}/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          const mePayload = (response?.data?.data) ?? response?.data;
          const userData = mePayload?.user;
          if (userData) {
            // Preserve current_artist_id from localStorage if backend omits it (e.g. stale deploy)
            let storedUser: User | null = null;
            try {
              const raw = localStorage.getItem('user');
              storedUser = raw ? JSON.parse(raw) : null;
            } catch { /* ignore */ }
            const fallbackArtistId = userData.last_selected_artist_id ?? storedUser?.current_artist_id ?? storedUser?.last_selected_artist_id;
            const merged = userData.current_artist_id === undefined && fallbackArtistId != null
              ? { ...userData, current_artist_id: fallbackArtistId, current_account_type: storedUser?.current_account_type ?? userData.current_account_type }
              : userData;
            setUser(merged);
            setToken(storedToken);
            // Keep localStorage in sync
            localStorage.setItem('user', JSON.stringify(merged));
          } else {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setUser(null);
            setToken(null);
          }
        } catch (error: unknown) {
          // Only clear storage on 401/403 (token invalid) - not on network errors.
          // Note: a plain expired-access-token 401 never reaches here anymore —
          // the response interceptor above already tried to refresh-and-retry
          // this exact request. Landing here means the refresh itself also
          // failed (refresh token missing/expired/revoked), so this really is
          // a "must log in again" case.
          const status = axios.isAxiosError(error) ? error.response?.status : null;
          if (status === 401 || status === 403) {
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('user');
            setUser(null);
            setToken(null);
          } else {
            // On network error or other failure, restore from localStorage so user stays logged in
            try {
              if (storedUser) {
                setUser(JSON.parse(storedUser));
              }
              setToken(storedToken);
            } catch {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
              setToken(null);
            }
          }
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; user: User }> => {
    try {
      const response = await axios.post(`${API_ENDPOINT}/auth/login`, {
        email,
        password
      });

      const loginPayload = (response?.data?.data) ?? response?.data;
      const { token: newToken, user: userData, refreshToken } = loginPayload;

      console.log('AuthContext - login response:', response.data);
      console.log('AuthContext - userData:', userData);

      localStorage.setItem('token', newToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      // Only store essential user data in localStorage to avoid quota issues
      const essentialUserData = {
        id: userData.id,
        email: userData.email,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        date_of_birth: userData.date_of_birth,
        location: userData.location,
        bio: userData.bio,
        website: userData.website,
        role: userData.role,
        artistname: userData.artistname,
        supportername: userData.supportername,
        fileurl: userData.fileurl,
        profile_photo_url: userData.profile_photo_url,
        current_account_type: userData.current_account_type,
        current_artist_id: userData.current_artist_id,
        last_selected_artist_id: userData.last_selected_artist_id
      };

      try {
        localStorage.setItem('user', JSON.stringify(essentialUserData));
      } catch (error) {
        console.error('Failed to store user data in localStorage:', error);
        localStorage.removeItem('user');
      }

      startTransition(() => {
        setToken(newToken);
        setUser(userData);
      });
      return { success: true, user: userData };
    } catch (error) {
      throw error;
    }
  };

  const loginWithGoogle = async (
    idToken: string,
    options?: {
      role?: 'artist' | 'supporter';
      promoCode?: string;
      artistname?: string;
      username?: string;
      first?: string;
      last?: string;
      location?: string;
      dob?: string;
    }
  ): Promise<{ success: boolean; user: User }> => {
    try {
      const response = await axios.post(`${API_ENDPOINT}/auth/google`, { idToken, ...options });
      const loginPayload = (response?.data?.data) ?? response?.data;
      const { token: newToken, user: userData, refreshToken } = loginPayload;

      localStorage.setItem('token', newToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      const essentialUserData = {
        id: userData.id,
        email: userData.email,
        username: userData.username,
        first_name: userData.first_name,
        last_name: userData.last_name,
        date_of_birth: userData.date_of_birth,
        location: userData.location,
        bio: userData.bio,
        website: userData.website,
        role: userData.role,
        artistname: userData.artistname,
        supportername: userData.supportername,
        fileurl: userData.fileurl,
        profile_photo_url: userData.profile_photo_url,
        current_account_type: userData.current_account_type,
        current_artist_id: userData.current_artist_id,
        last_selected_artist_id: userData.last_selected_artist_id
      };

      try {
        localStorage.setItem('user', JSON.stringify(essentialUserData));
      } catch (error) {
        console.error('Failed to store user data in localStorage:', error);
        localStorage.removeItem('user');
      }

      startTransition(() => {
        setToken(newToken);
        setUser(userData);
      });
      return { success: true, user: userData };
    } catch (error) {
      throw error;
    }
  };

  // Hydrates user/token state from an already-issued token (e.g. handed back
  // from the Electron desktop app's system-browser sign-in flow) without a
  // fresh /auth/login or /auth/google call — same GET /auth/me path checkAuth
  // uses on mount, just invoked on demand so callers don't need a full reload.
  const loginWithToken = async (newToken: string): Promise<{ success: boolean; user: User }> => {
    const response = await axios.get(`${API_ENDPOINT}/auth/me`, {
      headers: { Authorization: `Bearer ${newToken}` }
    });
    const mePayload = (response?.data?.data) ?? response?.data;
    const userData = mePayload?.user;
    if (!userData) {
      throw new Error('No user returned for provided token');
    }

    localStorage.setItem('token', newToken);
    localStorage.setItem('user', JSON.stringify(userData));
    startTransition(() => {
      setToken(newToken);
      setUser(userData);
    });
    return { success: true, user: userData };
  };

  const register = async (email: string, password: string, role: string): Promise<{ success: boolean; user: User }> => {
    try {
      const response = await axios.post(`${API_ENDPOINT}/auth/register`, {
        email,
        password,
        role
      });

      const registerPayload = (response?.data?.data) ?? response?.data;
      const { token: newToken, user: userData, refreshToken } = registerPayload;
      if (!newToken || !userData) {
        throw new Error('Registration completed but no session was returned.');
      }

      localStorage.setItem('token', newToken);
      if (refreshToken) localStorage.setItem('refreshToken', refreshToken);

      // Only store essential user data in localStorage to avoid quota issues
      const essentialUserData = {
        id: userData.id,
        email: userData.email,
        role: userData.role,
        artistname: userData.artistname,
        supportername: userData.supportername,
        fileurl: userData.fileurl,
        profile_photo_url: userData.profile_photo_url,
        bio: userData.bio,
        current_account_type: userData.current_account_type,
        current_artist_id: userData.current_artist_id,
        last_selected_artist_id: userData.last_selected_artist_id
      };
      
      try {
        localStorage.setItem('user', JSON.stringify(essentialUserData));
      } catch (error) {
        console.error('Failed to store user data in localStorage:', error);
        localStorage.removeItem('user');
      }
      
      startTransition(() => {
        setToken(newToken);
        setUser(userData);
      });
      return { success: true, user: userData };
    } catch (error) {
      throw error;
    }
  };

  const registerLogoutCallback = (callback: LogoutCallback): (() => void) => {
    setOnLogoutCallbacks(prev => [...prev, callback]);
    // Return a cleanup function to remove the callback
    return () => {
      setOnLogoutCallbacks(prev => prev.filter(cb => cb !== callback));
    };
  };

  const logout = (): void => {
    // Execute all logout callbacks (e.g., stop audio, clear states)
    onLogoutCallbacks.forEach(callback => {
      try {
        callback();
      } catch (error) {
        console.error('Error in logout callback:', error);
      }
    });
    
    const storedRefreshToken = localStorage.getItem('refreshToken');
    if (storedRefreshToken) {
      // Best-effort — don't block logout on it, and a failure here just means
      // the refresh token ages out naturally instead of being revoked immediately.
      axios.post(`${API_ENDPOINT}/auth/logout`, { refreshToken: storedRefreshToken }).catch(() => {});
    }

    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const updateUser = (updatedUser: User): void => {
    setUser((prev) => {
      const merged = prev ? { ...prev, ...updatedUser } : updatedUser;
      // Preserve current_artist_id when backend omits it (e.g. PUT /users/:id returning partial row)
      if (prev && updatedUser.current_artist_id === undefined && prev.current_artist_id != null) {
        merged.current_artist_id = prev.current_artist_id;
      }
      const essentialUserData = {
        id: merged.id,
        email: merged.email,
        username: merged.username,
        first_name: merged.first_name,
        last_name: merged.last_name,
        date_of_birth: merged.date_of_birth,
        location: merged.location,
        bio: merged.bio,
        website: merged.website,
        role: merged.role,
        artistname: merged.artistname,
        supportername: merged.supportername,
        fileurl: merged.fileurl,
        profile_photo_url: merged.profile_photo_url,
        current_account_type: merged.current_account_type,
        current_artist_id: merged.current_artist_id,
        last_selected_artist_id: merged.last_selected_artist_id
      };
      try {
        localStorage.setItem('user', JSON.stringify(essentialUserData));
      } catch (error) {
        console.error('Failed to store user data in localStorage:', error);
        localStorage.removeItem('user');
      }
      return merged;
    });
  };

  const switchAccountType = async (accountType: string, artistId: number | null = null): Promise<{ success: boolean }> => {
    try {
      const response = await axios.post(`${API_ENDPOINT}/users/${user.id}/switch-account`, {
        accountType,
        artistId
      });
      
      updateUser(response.data);
      return { success: true };
    } catch (error) {
      console.error('Error switching account type:', error);
      throw error;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    loginWithGoogle,
    register,
    loginWithToken,
    logout,
    updateUser,
    switchAccountType,
    registerLogoutCallback,
    isAuthenticated: !!token,
    isAdmin: getRoleFromToken(token) === 'admin'
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
